import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClockRotateLeft, faEdit, faTrash } from '@fortawesome/free-solid-svg-icons';
import { prettyDate } from '../utils/scheduleDates';
import '../styles/backdatedTask.css';

// How each tracked field is named in the history (server/utils/backdatedTasks.js).
const FIELD_LABELS = {
    title: 'Title',
    description: 'Details',
    workDate: 'Day',
    taskType: 'Type',
    projectId: 'Project',
    priority: 'Priority',
    startTime: 'Start time',
    dueTime: 'End time',
    timeAllottedMinutes: 'Minutes',
    assignedBy: 'Asked by'
};

const when = (at) => new Date(at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

// Ids are not worth showing; say that it changed instead.
const valueText = (field, value) => {
    if (!value) return '—';
    if (field === 'workDate') return prettyDate(value);
    if (field === 'projectId' || field === 'assignedBy') return null;
    return value;
};

/**
 * The status panel for back-dated work (a record of something already done),
 * with who may change it and its edit history. Used by pages/TaskDetail.js.
 */
const BackdatedTaskPanel = ({ task, onEditOwn, onRemove, onManage }) => {
    const access = task.backdatedAccess || {};
    const history = [...(task.editHistory || [])].reverse();
    return (
        <section className="td-panel">
            <header className="td-panel-head">
                <h2><FontAwesomeIcon icon={faClockRotateLeft} /> Back-dated work</h2>
            </header>
            <div className="td-panel-body">
                <p className="bd-line">
                    Done on <strong>{prettyDate(task.workDate)}</strong> and logged afterwards.
                </p>
                <p className="td-muted-line">
                    {task.approvalStatus === 'Approved'
                        ? 'Approved, so only Admin, HR or the team lead can change it now.'
                        : 'It can be changed or removed by the person who logged it until it is approved.'}
                </p>

                {(access.editOwn || access.manage || access.remove) && (
                    <div className="bd-actions">
                        {access.editOwn && (
                            <button type="button" className="gts-btn secondary" onClick={onEditOwn}>
                                <FontAwesomeIcon icon={faEdit} /> Edit
                            </button>
                        )}
                        {!access.editOwn && access.manage && (
                            <button type="button" className="gts-btn secondary" onClick={onManage}>
                                <FontAwesomeIcon icon={faEdit} /> Edit
                            </button>
                        )}
                        {access.remove && (
                            <button type="button" className="gts-btn secondary bd-danger" onClick={onRemove}>
                                <FontAwesomeIcon icon={faTrash} /> Remove
                            </button>
                        )}
                    </div>
                )}

                <h3 className="bd-history-title">Edit history</h3>
                {history.length === 0 ? (
                    <p className="td-muted-line">Not changed since it was logged.</p>
                ) : (
                    <ol className="bd-history">
                        {history.map((entry, i) => (
                            <li key={i}>
                                <div className="bd-history-head">
                                    <strong>{entry.by?.name || 'Someone'}</strong>
                                    {' '}{entry.action === 'created' ? 'logged it' : entry.action === 'removed' ? 'removed it' : 'changed it'}
                                    <span className="bd-history-when">{when(entry.at)}</span>
                                </div>
                                {(entry.changes || []).length > 0 && (
                                    <ul className="bd-history-changes">
                                        {entry.changes.map((c, j) => {
                                            const from = valueText(c.field, c.from);
                                            const to = valueText(c.field, c.to);
                                            return (
                                                <li key={j}>
                                                    {FIELD_LABELS[c.field] || c.field}
                                                    {from === null ? ' changed' : <>: <s>{from}</s> → {to}</>}
                                                </li>
                                            );
                                        })}
                                    </ul>
                                )}
                            </li>
                        ))}
                    </ol>
                )}
            </div>
        </section>
    );
};

export default BackdatedTaskPanel;
